import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (res.data.role === "admin") {
        navigate("/admin/dashboard");
      } else {
        setError("This account does not have admin access.");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm px-6"
      >
        <div className="text-center mb-8">
          <Heart className="w-10 h-10 text-[#C0603A] mx-auto mb-3" />
          <h1 className="font-heading text-3xl text-[#2C2C2A] font-medium">Admin Login</h1>
          <p className="font-body text-sm text-[#6C6B66] mt-1">333Oreo Foundation Dashboard</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 font-body text-sm text-red-600" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
            <input
              type="email"
              placeholder="Admin Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
              data-testid="admin-email-input"
              autoComplete="email"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
              data-testid="admin-password-input"
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-[#C0603A] text-white hover:bg-[#A64D29] disabled:opacity-60 transition-all rounded-full py-3.5 font-body font-semibold text-sm"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
